import { Info, X } from "lucide-react";
import {
	Fragment,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";

import { ApiError } from "@/api/error";
import { toast } from "@/app/store/toast.store";
import {
	getFrequencyClass,
	type ProItem,
} from "@/features/patients/pro-items";
import {
	getWeekDateRange,
	getWeekDateRangeFull,
	type ProDayAnswer,
	type ProWeekResponse,
} from "@/features/patients/pro-responses";
import {
	useFlatProItems,
	useProCellDetail,
	useProItems,
	useProPhoto,
	useProResponses,
} from "@/shared/hooks/use-pro-data";

const TOTAL_WEEKS = 26;

interface Props {
	patientUid: string;
	/** 0-based index of the "current" week to highlight. */
	currentWeek?: number;
	/** Treatment start (discharge date, ISO yyyy-MM-dd) — anchors the week-column dates. */
	startDate?: string;
}

interface PopoverState {
	item: ProItem;
	weekIndex: number;
	response: ProWeekResponse;
	x: number;
	y: number;
	/** If true, popover flipped to show above the anchor. */
	flipped: boolean;
}

/** Normalise a grade into a CSS modifier. */
function gradeClass(g: 1 | 2 | 3 | null): string {
	if (g === null) return "grade-chip--missed";
	return `grade-chip--g${g}`;
}

/**
 * One PRO answer photo. `src` is the server's care-team-gated proxy URL, not a loadable image URL — this
 * component fetches it through the authenticated `httpClient` (via `useProPhoto`) and swaps in the resolved
 * base64 `data:` URI once it arrives, instead of handing the proxy URL straight to `<img src>` (which would
 * 401 every time: a browser image load can never attach the bearer token the endpoint requires).
 *
 * A 404 from the proxy is ambiguous by design (anti-enumeration, `MedicalProService.photo`): it covers both
 * "no such patient" and "caller is on the institution but not this patient's care team". Only the latter is
 * something the viewer can act on, so a `PATIENT_NOT_FOUND` here — always reachable only via the care-team
 * gate, since the popover only opens for a patient already loaded — surfaces a one-time toast explaining why
 * the photo stays blank, instead of leaving an unexplained empty tile indistinguishable from "still loading".
 */
function ProPhotoImg({ src, alt }: { src: string; alt: string }) {
	const { t } = useTranslation(["medical"]);
	const { data: dataUri, error, isPending, isError } = useProPhoto(src);

	useEffect(() => {
		if (error instanceof ApiError && error.code === "PATIENT_NOT_FOUND") {
			// Reuses the existing "not your assigned patient" copy (patients.errors.accessDenied) rather than
			// minting new wording — same underlying reason (care-team scope), just surfaced per-photo here
			// instead of at the whole-page level.
			toast.warning(t("medical:patients.errors.accessDenied"));
		}
		// `t` deliberately excluded: it is not stable across renders (i18next issues a new function on
		// re-render/namespace load), and re-firing the toast when only the translator identity changes —
		// while `error` itself has not — would warn the viewer twice for one underlying failure.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	if (isPending || isError) {
		return <div className="pro-answer-photo__placeholder" aria-hidden="true" />;
	}
	return <img src={dataUri} alt={alt} />;
}

export function ProCalendar({
	patientUid,
	currentWeek = -1,
	startDate,
}: Props) {
	const { t } = useTranslation(["medical", "common"]);

	// Week columns are anchored to the Monday of the discharge week (PRD §3.2 "시작 월요일 날짜",
	// matching the prototype). Without this the labels fall back to a wrong default base (6 months ago).
	const weekBase = useMemo(() => {
		if (!startDate) return undefined;
		const d = new Date(startDate + "T00:00:00");
		if (Number.isNaN(d.getTime())) return undefined;
		const dow = d.getDay(); // 0=Sun … 6=Sat
		d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow)); // back to Monday
		return d;
	}, [startDate]);
	const [popover, setPopover] = useState<PopoverState | null>(null);
	// PD-02 v2.1: attached-photo lightbox.
	const [lightbox, setLightbox] = useState<{
		url: string;
		caption?: string;
	} | null>(null);
	const [gradeLegendOpen, setGradeLegendOpen] = useState(false);
	const [gradeLegendPos, setGradeLegendPos] = useState<{
		left: number;
		top: number;
	}>({ left: 0, top: 0 });
	const popoverRef = useRef<HTMLDivElement>(null);
	const gradeInfoBtnRef = useRef<HTMLButtonElement>(null);

	// The item master, the grid and the treatment bar all come from ONE server call — the master is
	// admin-owned, so this screen consumes it rather than keeping its own copy.
	const items = useFlatProItems(patientUid);
	const { data: responseMap } = useProResponses(patientUid);
	const { data: proAreas = [] } = useProItems(patientUid);

	// The open cell's day-by-day detail. Fetched only while a cell is open, and ONLY from the server: until this
	// existed the panel was filled by a random-number generator, and showing a clinician invented answers for a
	// real patient is far worse than showing them none.
	const {
		data: popoverDays = [],
		isPending: popoverDaysPending,
		isError: popoverDaysError,
	} = useProCellDetail(patientUid, popover?.item.id, popover?.weekIndex);

	// SHCT-935 — a failed read is a System error (§7.1 → toast), reusing the grid's own copy
	// (patients.errors.loadPro) instead of new wording. Without this the empty `data` fell through to
	// "nothing was due", which states something false about the patient.
	useEffect(() => {
		if (popoverDaysError) toast.error(t("medical:patients.errors.loadPro"));
		// `t` excluded for the same reason as ProPhotoImg: a new translator identity must not re-fire the toast.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [popoverDaysError]);

	// Close popover on outside click / Escape
	const closePopover = useCallback(() => setPopover(null), []);

	useEffect(() => {
		if (!popover) return;
		// While the lightbox is open it is the topmost layer, and only it answers Escape and outside clicks
		// (SHCT-741). Both dismissals are bound to `document`, so without this the two layers fire together:
		// one Escape closed the popover AND the lightbox, and every click on the lightbox — its backdrop, its
		// close button, even the photo itself — landed outside `popoverRef` and tore down the popover the
		// viewer was reading. Closing the lightbox is meant to return them to it.
		if (lightbox) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") closePopover();
		};
		// Outside-close on `pointerdown` (fires BEFORE the click that opens a popover), so the
		// opening interaction never closes its own popover — no `setTimeout` deferral needed.
		// The old setTimeout(addEventListener('click')) leaked a document listener whenever the
		// effect cleaned up before the timer fired (guaranteed under StrictMode). React 18 flushes
		// the discrete click synchronously before it reaches document, so a leaked listener saw the
		// just-opened popover and instantly closed it — the popover would stop opening after a few
		// toggles and stay broken across patients (listener lives on document; this never unmounts).
		const onPointerDown = (e: PointerEvent) => {
			if (
				popoverRef.current &&
				!popoverRef.current.contains(e.target as Node)
			) {
				closePopover();
			}
		};
		document.addEventListener("keydown", onKey);
		document.addEventListener("pointerdown", onPointerDown);
		return () => {
			document.removeEventListener("keydown", onKey);
			document.removeEventListener("pointerdown", onPointerDown);
		};
	}, [popover, lightbox, closePopover]);

	// Switching patients keeps this component mounted (no `key`), so clear any open popover/lightbox
	// from the previous patient — otherwise a stale cell detail would linger over the new calendar.
	useEffect(() => {
		setPopover(null);
		setLightbox(null);
		setGradeLegendOpen(false);
	}, [patientUid]);

	// Close the photo lightbox on Escape.
	useEffect(() => {
		if (!lightbox) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setLightbox(null);
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [lightbox]);

	// After popover mounts, measure actual height synchronously and adjust position
	// before the browser paints — eliminates flicker.
	const [popoverReady, setPopoverReady] = useState(false);
	// SHCT-567: caps the flip below at ONE attempt per popover-open. Without this, `setPopover`
	// creates a new `popover` object every flip, which re-triggers this same effect (it depends on
	// `popover`); if the flipped position STILL overflows (a very tall panel, a narrow viewport, or
	// geometry made stale by a fast unmount/remount racing a tab switch while this popover was still
	// open), `rect.bottom > window.innerHeight - 4` stays true forever and the effect keeps calling
	// setState on every commit — React's "Maximum update depth exceeded" (minified error #185),
	// reproduced live by rapidly switching tabs while a popover was open.
	const hasFlippedRef = useRef(false);
	// Identity of the popover this ref/guard state belongs to — "item id : week index", NOT the
	// `popover` object's reference. A content-identical-but-new `popover` object (or a `popoverDays`
	// array that changes reference without changing content) must NOT be treated as "a different
	// popover reopened"; only a genuinely different cell being clicked resets these guards.
	const settledForKeyRef = useRef<string | null>(null);
	useLayoutEffect(() => {
		if (!popover) {
			setPopoverReady(false);
			hasFlippedRef.current = false;
			settledForKeyRef.current = null;
			return;
		}
		const popoverKey = `${popover.item.id}:${popover.weekIndex}`;
		if (settledForKeyRef.current === popoverKey) {
			// Already measured/flipped/readied for this exact popover — nothing left to do. Without
			// this the effect kept re-running (and re-calling setPopoverReady/setPopover) on every
			// commit even after settling, once something (a parent re-render, a re-created-but-
			// content-identical popoverDays array) kept re-triggering it — React's "Maximum update
			// depth exceeded" (minified error #185), reproduced live by rapidly switching tabs while
			// a popover was open.
			return;
		}
		const el = popoverRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		const neededHeight = rect.height;
		if (!hasFlippedRef.current && rect.bottom > window.innerHeight - 4) {
			// Flip above — use the original anchor bottom as reference. Only ever done once per
			// popover-open: if the flipped position still overflows, that is accepted rather than
			// re-flipping indefinitely (a fixed, imperfect position beats an infinite update loop).
			hasFlippedRef.current = true;
			const anchorBottom = popover.y;
			const newY = Math.max(8, anchorBottom - neededHeight - 16);
			setPopover((prev) => (prev ? { ...prev, y: newY, flipped: true } : null));
			// The state update above will re-run this effect once more (to re-measure the flipped
			// position); settle only AFTER that follow-up run, not this one.
			el.offsetHeight;
			setPopoverReady(true);
			return;
		}
		settledForKeyRef.current = popoverKey;
		// Force a synchronous paint cycle by reading offsetHeight (triggers reflow)
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		el.offsetHeight;
		setPopoverReady(true);
		// `popoverDays` is in the dependency list because the days arrive AFTER the popover opens: the panel is
		// short while they load and grows once they land, so a popover near the bottom of the screen has to be
		// re-measured and re-flipped at that point or it renders off-screen (still subject to the
		// one-flip-per-open cap above).
	}, [popover, popoverDays]);

	// ── Cell click → open popover ──
	const handleCellClick = (
		item: ProItem,
		weekIndex: number,
		response: ProWeekResponse,
		e: React.MouseEvent<HTMLTableCellElement>,
	) => {
		const rect = e.currentTarget.getBoundingClientRect();
		const pw = 360;
		// A first guess only — the days are still being fetched, so their real count is not known yet. The layout
		// effect above re-measures and re-positions once they arrive.
		const ph = 56 + 28 + 76 + 24;
		// Horizontal: clamp within viewport (12px margin)
		let x = rect.left;
		if (x + pw > window.innerWidth - 12) x = window.innerWidth - pw - 12;
		if (x < 12) x = 12;
		// Default below
		let y = rect.bottom + 8;
		let flipped = false;
		if (y + ph > window.innerHeight - 12) {
			y = Math.max(8, rect.top - ph - 8);
			flipped = true;
		}
		setPopover({ item, weekIndex, response, x, y, flipped });
	};

	// ── Grade info button → position popover below button ──
	const handleGradeInfoClick = useCallback(() => {
		if (gradeInfoBtnRef.current) {
			const r = gradeInfoBtnRef.current.getBoundingClientRect();
			const w = 340;
			setGradeLegendPos({
				left: Math.max(12, Math.min(r.left, window.innerWidth - w - 12)),
				top: r.bottom + 6,
			});
		}
		setGradeLegendOpen((v) => !v);
	}, []);

	// Group items by area
	const itemsByArea = useMemo(() => {
		const map = new Map<number, ProItem[]>();
		for (const item of items) {
			const arr = map.get(item.areaIndex) ?? [];
			arr.push(item);
			map.set(item.areaIndex, arr);
		}
		return map;
	}, [items]);

	// Week labels & ranges
	const weekHeaders = useMemo(() => {
		const headers: { label: string; range: string; isCurrent: boolean }[] = [];
		for (let w = 0; w < TOTAL_WEEKS; w++) {
			headers.push({
				label: `W${w + 1}`,
				range: getWeekDateRange(w, weekBase),
				isCurrent: w === currentWeek,
			});
		}
		return headers;
	}, [currentWeek, weekBase]);

	// Render ONE answer by its type. A PRO item is a set of questions, so a day carries several of these — every
	// one of them is drawn, because showing only the first would hide the rest of what the patient told us.
	const renderAnswer = (d: ProDayAnswer) => {
		const label = t("medical:patients.pro.popover.responseLabel");
		const at = d.answerType ?? "single";
		if (at === "multi" && d.multi?.length) {
			// Prototype `_proAnswerHtml`: its own label, and the chips on their OWN row beneath it — inline
			// after "응답:" they read as one wrapped sentence, which is how a multi-select passed for a
			// single choice on screen even once the selections were being grouped (SHCT-741).
			return (
				<div className="pro-day-block__answer">
					<strong>{t("medical:patients.pro.popover.multiLabel")}</strong>
					<div className="pro-answer-chips">
						{d.multi.map((m, i) => (
							<span key={i} className="pro-answer-chip">
								{m}
							</span>
						))}
					</div>
				</div>
			);
		}
		const written = d.textValue || d.answerText;
		if (at === "text" && written) {
			// The written-answer branch the prototype has and this renderer never did: without it a TEXT
			// answer fell through to the default arm and was printed inline after "응답:", so a paragraph
			// the patient wrote was drawn exactly like a one-word 단답.
			//
			// The body comes from `textValue`, its own field. It used to be read from `answerText`, which a
			// written answer never carries — the patient's app sends no option label for one — so this branch
			// never fired on real data and the body surfaced in the 기타 block instead. `answerText` is kept
			// only as a fallback, for a question type that does carry a label yet is rendered as text.
			return (
				<div className="pro-day-block__answer">
					<strong>{t("medical:patients.pro.popover.textLabel")}</strong>
					<div className="pro-answer-textbox">{written}</div>
				</div>
			);
		}
		if (at === "slider" && d.sliderValue != null) {
			const min = d.sliderMin ?? 0;
			const max = d.sliderMax ?? 10;
			const pct =
				max > min
					? Math.round(
							Math.max(0, Math.min(100, ((d.sliderValue - min) / (max - min)) * 100)),
						)
					: 0;
			// The range caption is printed ONLY when the server actually sent the scale. The gauge still
			// falls back to 0~10 so the bar has a geometry to draw, but writing "0~10" beside the value
			// would state a scale as fact when the question's sliderConfig is unwired — and 7 on 0~10
			// against 7 on 0~100 is a different patient. The prototype prints it unconditionally because
			// its mock data always carries a range; real answers do not.
			const hasRange = d.sliderMin != null && d.sliderMax != null;
			return (
				<div className="pro-day-block__answer">
					<strong>{label}</strong>{" "}
					<b>
						{d.sliderValue}
						{d.sliderUnit ?? ""}
					</b>{" "}
					<span className="pro-answer-gauge">
						<span className="pro-answer-gauge__fill" style={{ width: `${pct}%` }} />
					</span>{" "}
					{hasRange && (
						<span className="pro-answer-gauge__range">
							{d.sliderMin}~{d.sliderMax}
						</span>
					)}
				</div>
			);
		}
		if (at === "battery" && d.battery?.length) {
			return (
				<div className="pro-day-block__answer">
					<strong>
						{t("medical:patients.pro.popover.batteryLabel", {
							count: d.battery.length,
						})}
					</strong>{" "}
					{d.batteryAvg != null && (
						<span className="pro-answer-battery-avg">
							{t("medical:patients.pro.popover.batteryAvg", {
								avg: d.batteryAvg.toFixed(1),
							})}
						</span>
					)}
					{/* Kept a <ul>: it IS a list of sub-items, and the prototype's plain <div> rows carry no
					    meaning a screen reader can use. The prototype's look comes from the CSS instead. */}
					<ul className="pro-answer-battery">
						{d.battery.map((b, i) => (
							<li key={i}>
								<span className="pro-answer-battery__label">{b.label}</span>
								{/* The prototype also appends a `· {valueLabel}` scale name here. Nothing supplies
								    one — the server stores no name for a battery sub-item OR for its scale steps
								    (itemName is always null) — so the value stands alone rather than beside an
								    invented label. Part of the open SHCT-741 question on score vs scaleIdx. */}
								<span className="pro-answer-battery__value">{b.value}</span>
							</li>
						))}
					</ul>
				</div>
			);
		}
		// single (default) — 단답 · Yes/No · O/X · 사진, all of which are one chosen label. TEXT no longer
		// lands here; it has its own branch above.
		return d.answerText ? (
			<div className="pro-day-block__answer">
				<strong>{label}</strong> {d.answerText}
			</div>
		) : null;
	};

	return (
		<div className="card pro-cal-card" style={{ marginTop: 12 }}>
			<div className="card__header">
				<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
					<h3 className="card__title">
						{t("medical:patients.pro.calendar.title")}
					</h3>
					<button
						ref={gradeInfoBtnRef}
						type="button"
						className="pro-grade-info"
						onClick={handleGradeInfoClick}
						aria-label={t("medical:patients.pro.gradePopover.title")}
						title={t("medical:patients.pro.gradePopover.title")}
					>
						<Info size={14} />
					</button>
				</span>
				{/* PD-02 §3.2: 4-type legend in the card header */}
				<span className="pro-cal__legend">
					<span className="pro-cal__legend-item">
						<span className="grade-chip grade-chip--g1" />
						{t("medical:patients.pro.legend.chips.g1")}
					</span>
					<span className="pro-cal__legend-item">
						<span className="grade-chip grade-chip--g2" />
						{t("medical:patients.pro.legend.chips.g2")}
					</span>
					<span className="pro-cal__legend-item">
						<span className="grade-chip grade-chip--g3" />
						{t("medical:patients.pro.legend.chips.g3")}
					</span>
					<span className="pro-cal__legend-item">
						<span className="grade-chip grade-chip--missed" />
						{t("medical:patients.pro.legend.chips.incomplete")}
					</span>
				</span>
			</div>
			<div
				className="card__body"
				style={{ overflowX: "auto", padding: "0 0 12px" }}
			>
				<table className="pro-cal">
					{/* ── Week header row ── */}
					<thead>
						<tr>
							<th className="pro-cal__corner">
								{t("medical:patients.pro.calendar.columnItem")}
							</th>
							{weekHeaders.map((wh, i) => (
								<th
									key={i}
									className={`pro-cal__week-head${wh.isCurrent ? " pro-cal__week-head--current" : ""}`}
								>
									{wh.label}
									<span className="pro-cal__week-date">
										{wh.range.split("–")[0]}
									</span>
								</th>
							))}
						</tr>
					</thead>

					{/* ── Body: areas → items → cells ── */}
					<tbody>
						{proAreas.map((area) => {
							const areaItems = itemsByArea.get(area.index) ?? [];
							return (
								<Fragment key={area.index}>
									{/* Area group row */}
									<tr className="pro-cal__area-row">
										<td
											className="pro-cal__area-cell"
											colSpan={TOTAL_WEEKS + 1}
										>
											<span className="pro-cal__area-label">{area.label}</span>
										</td>
									</tr>

									{/* Item rows */}
									{areaItems.map((item) => (
										<tr key={item.id}>
											{/* Item name + frequency chip */}
											<td className="pro-cal__item">
												<div className="pro-cal__item-row">
													<span className="pro-cal__item-name">
														{item.name}
													</span>
													<span
														className={`pro-chip ${getFrequencyClass(item.frequency)}`}
													>
														{item.frequencyLabel}
													</span>
												</div>
											</td>

											{/* 26 week data cells */}
											{Array.from({ length: TOTAL_WEEKS }, (_, w) => {
												const key = `${item.id}:${w}`;
												const resp = responseMap?.get(key);
												const isCurrent = w === currentWeek;
												const status = resp?.status ?? "none";
												const isFuture = currentWeek >= 0 && w > currentWeek;

												// "Was anything due this week?" is the SERVER's answer, not ours. It knows the
												// discharge date, the item's first-execution rule and its weekdays; this screen
												// knows none of those. A second copy of that rule here would eventually disagree
												// with the patient's own app about whether a check-in had been missed.
												const scheduled = status !== "none";

												// Status label for tooltip
												const statusLabel =
													status === "none"
														? t("medical:patients.pro.status.none")
														: status === "future"
															? t("medical:patients.pro.status.future")
															: status === "missed"
																? t("medical:patients.pro.status.missed")
																: status === "completed"
																	? t("medical:patients.pro.status.completed")
																	: status === "partial"
																		? t("medical:patients.pro.status.partial")
																		: status === "future-expected"
																			? t(
																					"medical:patients.pro.status.futureExpected",
																				)
																			: "";

												const tooltip =
													scheduled || resp
														? `${item.name} W${w + 1} (${getWeekDateRangeFull(w, weekBase)})${statusLabel ? ` — ${statusLabel}` : ""}`
														: undefined;

												// Cell class
												let cellCls = `pro-cal__cell`;
												if (status !== "none")
													cellCls += ` pro-cal__cell--${status}`;
												if (isCurrent) cellCls += " pro-cal__cell--current";

												// A cell opens its detail only when something actually happened in that week.
												const clickable =
													status === "responded" ||
													status === "completed" ||
													status === "missed" ||
													status === "partial" ||
													status === "future-expected";

												// Grade chip rendering
												const hasGrade = resp?.grade != null;
												const plusMod =
													item.frequency === "daily" && hasGrade
														? " grade-chip--plus"
														: "";

												return (
													<td
														key={key}
														className={cellCls}
														title={tooltip}
														onClick={
															clickable
																? (e) => handleCellClick(item, w, resp!, e)
																: undefined
														}
													>
														{status === "future" ||
														status === "future-expected" ? null : hasGrade ? (
															<span
																className={`grade-chip ${gradeClass(resp!.grade)}${plusMod}`}
															>
																{t(`medical:patients.pro.gradeShort.g${resp!.grade}`)}
															</span>
														) : status === "completed" ? (
															// Answered, but this question is pass/fail — there is no band to show. Drawn
															// as its own chip so it can never be mistaken for the dash below, which means
															// the patient did NOT answer.
															<span className="grade-chip grade-chip--completed">
																{t("medical:patients.pro.status.completed")}
															</span>
														) : status === "missed" || status === "partial" ? (
															<span className="grade-chip grade-chip--missed">
																—
															</span>
														) : null}
													</td>
												);
											})}
										</tr>
									))}
								</Fragment>
							);
						})}
					</tbody>
				</table>{" "}
			</div>

			{/* ── Grade legend popover ── */}
			{gradeLegendOpen && (
				<GradeLegendPopover
					left={gradeLegendPos.left}
					top={gradeLegendPos.top}
					onClose={() => setGradeLegendOpen(false)}
				/>
			)}
			{/* ── Response detail popover ── */}
			{popover && (
				<div
					ref={popoverRef}
					className="pro-popover"
					style={{
						left: popover.x,
						top: popover.y,
						visibility: popoverReady ? "visible" : "hidden",
					}}
				>
					<button
						type="button"
						className="pro-popover__close"
						onClick={closePopover}
						aria-label={t("medical:patients.pro.popover.closeAria")}
					>
						<X size={16} />
					</button>
					<div className="pro-response-detail--popover">
						<div className="pro-response-detail__header">
							<strong>{popover.item.name}</strong>
							<span className="pro-cal__cyc">
								({popover.item.frequencyLabel})
							</span>
							<span className="pro-response-detail__date">
								W{popover.weekIndex + 1} ·{" "}
								{popover.response.weekStart.slice(5).replace("-", "/")} ~{" "}
								{popover.response.weekEnd.slice(5).replace("-", "/")}
							</span>
						</div>

						{/* Cycle note */}
						<div className="pro-day-block-cycle">
							{popover.item.frequency === "daily"
								? t("medical:patients.pro.cycleNote.daily")
								: popover.item.frequency === "weekly"
									? t("medical:patients.pro.cycleNote.weekly")
									: popover.item.frequency === "biweekly"
										? t("medical:patients.pro.cycleNote.biweekly")
										: popover.item.frequency === "monthly"
											? t("medical:patients.pro.cycleNote.monthly")
											: popover.item.frequency === "quarterly"
												? t("medical:patients.pro.cycleNote.quarterly")
												: ""}
						</div>

						{/* Day-by-day list — from the server. Never generated. */}
						<div className="pro-day-block-list">
							{popoverDaysPending || popoverDaysError ? (
								// While the days are in flight the panel stays blank — matching how the rest of this
								// app renders a pending read. It must NOT fall through to the "nothing was due"
								// message below: that is a statement of fact about the patient, and asserting it
								// before the answer arrives would tell the clinician something we do not yet know.
								<div
									className="pro-response-detail__hint"
									style={{ padding: "12px 0" }}
								/>
							) : popoverDays.length === 0 ? (
								<div
									className="pro-response-detail__hint"
									style={{ padding: "12px 0" }}
								>
									{t("medical:patients.pro.popover.noExpectedDay")}
								</div>
							) : (
								popoverDays.map((d, i) => {
									if (d.status === "missed") {
										return (
											<div
												key={i}
												className="pro-day-block pro-day-block--missed"
											>
												<div className="pro-day-block__head">
													<span className="pro-day-block__date">
														{d.label} · {d.date.replace(/-/g, ".")}
													</span>
													<span className="pro-day-block__chip">
														{t("medical:patients.pro.popover.noResponse")}
													</span>
												</div>
											</div>
										);
									}
									if (d.status === "future-expected") {
										return (
											<div
												key={i}
												className="pro-day-block pro-day-block--future"
											>
												<div className="pro-day-block__head">
													<span className="pro-day-block__date">
														{d.label} · {d.date.replace(/-/g, ".")}
													</span>
													<span className="pro-day-block__chip">
														{t(
															"medical:patients.pro.popover.futureExpectedDay",
														)}
													</span>
												</div>
											</div>
										);
									}
									// The patient answered this day.
									//
									// A null grade does NOT mean they skipped it. A completion-type item (the
									// self-efficacy questionnaire) is answered in full and is simply pass/fail, so it
									// has no band to report.
									//
									// Each band is matched EXPLICITLY and anything else draws NO chip. There is
									// deliberately no fall-through arm: a chain ending in "...else G3" would render a
									// patient who COMPLETED their check-in as one scoring G3 — the worst possible
									// reading of the best possible behaviour — and nothing would throw, so no test
									// would catch it. The answers listed below are what show the day was answered.
									const gCls =
										d.level === 1
											? "grade-chip--g1"
											: d.level === 2
												? "grade-chip--g2"
												: d.level === 3
													? "grade-chip--g3"
													: null;
									const gLabel =
										d.level === 1 || d.level === 2 || d.level === 3
											? t(`medical:patients.pro.gradeShort.g${d.level}`)
											: null;
									return (
										<div
											key={i}
											className="pro-day-block pro-day-block--responded"
										>
											<div className="pro-day-block__head">
												<span className="pro-day-block__date">
													{d.label} · {d.date.replace(/-/g, ".")}
												</span>
												{gLabel && (
													<span
														className={`grade-chip ${gCls}`}
														data-testid="pro-day-grade"
													>
														{/* Grade code only (G1/G2/G3), colored — no score value (matches prototype). */}
														{gLabel}
													</span>
												)}
											</div>
											{/* EVERY answer of the item, not just the first — an item is a set of
											    questions, and hiding the rest would hide what the patient said. */}
											{(d.answers ?? []).map((a, ai) => (
												<Fragment key={ai}>
													{renderAnswer(a)}
													{a.otherText && (
														<div className="pro-day-block__free">
															<strong>
																{t("medical:patients.pro.popover.otherLabel")}
															</strong>{" "}
															{a.otherText}
														</div>
													)}
													{a.photos && a.photos.length > 0 && (
														<div className="pro-answer-photos">
															{a.photos.map((p, pi) => (
																<button
																	key={pi}
																	type="button"
																	className="pro-answer-photo"
																	onClick={() => setLightbox(p)}
																	aria-label={p.caption || `photo ${pi + 1}`}
																>
																	<ProPhotoImg src={p.url} alt={p.caption || ""} />
																</button>
															))}
														</div>
													)}
												</Fragment>
											))}
										</div>
									);
								})
							)}
						</div>
					</div>
				</div>
			)}

			{/* PD-02 v2.1: attached-photo lightbox */}
			{lightbox && (
				<div
					className="pro-lightbox"
					role="dialog"
					aria-modal="true"
					onClick={() => setLightbox(null)}
				>
					<div
						className="pro-lightbox__inner"
						onClick={(e) => e.stopPropagation()}
					>
						<button
							type="button"
							className="pro-lightbox__close"
							onClick={() => setLightbox(null)}
							aria-label={t("common:actions.close")}
						>
							<X size={20} />
						</button>
						<ProPhotoImg src={lightbox.url} alt={lightbox.caption || ""} />
						{lightbox.caption && (
							<div className="pro-lightbox__caption">{lightbox.caption}</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}

/** Grade legend popover — matches prototype _ensureProGradePopover. */
function GradeLegendPopover({
	left,
	top,
	onClose,
}: {
	left: number;
	top: number;
	onClose: () => void;
}) {
	const { t } = useTranslation(["medical"]);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		const onClick = (e: MouseEvent) => {
			if (
				ref.current &&
				!ref.current.contains(e.target as Node) &&
				!(e.target as HTMLElement).closest?.(".pro-grade-info")
			) {
				onClose();
			}
		};
		document.addEventListener("keydown", onKey);
		setTimeout(() => document.addEventListener("click", onClick), 0);
		return () => {
			document.removeEventListener("keydown", onKey);
			document.removeEventListener("click", onClick);
		};
	}, [onClose]);

	// SHCT-844 — PRD 04 v2.17 §3.2 fixes this popover at one sentence per 단계 and forbids a score
	// band. The band it used to print (0~10 / 11~20 / 21점 이상) was a single fixed cutoff, but the
	// Grade cutoff is configured per PRO item (어드민 PRD 04 §3.3), so it stated as fact something
	// that is only true of some items. A cell is the representative 단계 for that week across several
	// items ([MED.011]); the per-item numbers live in the response detail behind a cell click.
	const gradeSteps = [
		{
			step: t("medical:patients.pro.gradePopover.step1.step"),
			meaningKey: "medical:patients.pro.gradePopover.step1.meaning",
			cls: "grade-chip--g1",
		},
		{
			step: t("medical:patients.pro.gradePopover.step2.step"),
			meaningKey: "medical:patients.pro.gradePopover.step2.meaning",
			cls: "grade-chip--g2",
		},
		{
			step: t("medical:patients.pro.gradePopover.step3.step"),
			meaningKey: "medical:patients.pro.gradePopover.step3.meaning",
			cls: "grade-chip--g3",
		},
	];

	return (
		<div
			ref={ref}
			className="r-popover pro-grade-popover"
			role="dialog"
			aria-label={t("medical:patients.pro.gradePopover.title")}
			style={{
				position: "fixed",
				left,
				top,
				width: 340,
				zIndex: 1101,
			}}
		>
			<button
				type="button"
				className="r-popover__close"
				onClick={onClose}
				aria-label={t("medical:patients.pro.popover.closeAria")}
			>
				<X size={16} />
			</button>
			<p className="r-popover__title">
				<Info size={16} style={{ marginRight: 6 }} />
				{t("medical:patients.pro.gradePopover.title")}
			</p>
			<div style={{ fontSize: 13, color: "#4b5563", marginBottom: 12 }}>
				<Trans
					i18nKey="medical:patients.pro.gradePopover.description"
					components={{ strong: <strong /> }}
				/>
			</div>
			<ul className="pro-grade-popover__list">
				{gradeSteps.map((gs, i) => (
					<li key={i}>
						<strong>
							{gs.step}(
							<span className={`grade-chip ${gs.cls}`}>
								{t(`medical:patients.pro.gradeShort.g${i + 1}`)}
							</span>)
						</strong>{" "}
						·{" "}
						<Trans i18nKey={gs.meaningKey} components={{ strong: <strong /> }} />
					</li>
				))}
			</ul>
			{/* The old note cited a 2026.05.22 source for the score bands this popover no longer
			    carries. §3.2 replaces it with where the per-item numbers actually are. */}
			<div className="r-popover__note">
				{t("medical:patients.pro.gradePopover.perItemNote")}
			</div>
		</div>
	);
}
